import { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Button, makeStyles, tokens } from "@fluentui/react-components";
import { Spinner } from "@fluentui/react-components";
import { ApiClient } from "./api/client";
import { ApiClientProvider } from "./api/context";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { useOidcAuth } from "./auth/useOidcAuth";
import { getUserManager } from "./auth/oidcConfig";
import { Layout } from "@/layouts";
import { DefaultRedirect, LoadingFallback } from "@/components/common";
import { SearchCommand } from "@heke/ui-components";
import { useTasks } from "@/hooks";

const ICHomePage = lazy(() => import("@/pages/home/ICHomePage"));
const SupervisorHomePage = lazy(
  () => import("@/pages/supervisor/SupervisorHomePage"),
);
const ExecutiveDashboardPage = lazy(
  () => import("@/pages/executive/ExecutiveDashboardPage"),
);
const MyQueuePage = lazy(() => import("@/pages/queue/MyQueuePage"));
const AccountStatementPage = lazy(
  () => import("@/pages/accounts/AccountStatementPage"),
);
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ComponentShowcasePage = lazy(
  () => import("@/pages/admin/ComponentShowcasePage"),
);
const ObligorSearchPage = lazy(
  () => import("@/pages/obligor/ObligorSearchPage"),
);
const ObligorDetailPage = lazy(
  () => import("@/pages/obligor/ObligorDetailPage"),
);

const useStyles = makeStyles({
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
});

function Login() {
  const styles = useStyles();

  const handleLogin = () => {
    getUserManager().signinRedirect().catch(console.error);
  };

  return (
    <div className={styles.centered}>
      <h1>PriorityOS</h1>
      <Button appearance="primary" size="large" onClick={handleLogin}>
        Sign In
      </Button>
    </div>
  );
}

function AuthenticatedApp() {
  const client = new ApiClient();
  const navigate = useNavigate();
  const { tasks } = useTasks();
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  return (
    <ApiClientProvider client={client}>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="/home" element={<ICHomePage />} />
            <Route path="/dashboard" element={<ExecutiveDashboardPage />} />
            <Route path="/queue" element={<MyQueuePage />} />
            <Route path="/team" element={<SupervisorHomePage />} />
            <Route path="/accounts" element={<AccountStatementPage />} />
            <Route path="/obligors" element={<ObligorSearchPage />} />
            <Route
              path="/obligors/:obligorId"
              element={<ObligorDetailPage />}
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRoles={["executive-director"]}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/components"
              element={<ComponentShowcasePage />}
            />
          </Route>
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>

        <SearchCommand
          open={commandOpen}
          onClose={() => setCommandOpen(false)}
          tasks={tasks}
          onTaskSelect={() => {
            setCommandOpen(false);
            navigate("/");
          }}
          onCommandSelect={() => setCommandOpen(false)}
        />
      </Suspense>
    </ApiClientProvider>
  );
}

function App() {
  const { isAuthenticated, isLoading } = useOidcAuth();

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  return isAuthenticated ? <AuthenticatedApp /> : <Login />;
}

export default App;
